import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { OTPInput } from '@/components/ui/otp-input';
import { useLanguage } from '@/contexts/LanguageContext';
import { useOTPMethod } from '@/hooks/use-otp-method';
import { useAuth } from '@/contexts/AuthContext';
import { updateUserPhoneComprehensive } from '@/lib/firebase/queries/phone-management';
import { sendOTP, verifyOTP, resendOTP, cleanupOTP } from '@/lib/services/otp-verification';
import { toast } from 'sonner';
import { Loader2, Phone, MessageSquare, CheckCircle, ArrowLeft, Edit } from 'lucide-react';

type PhoneStep = 'display' | 'edit' | 'otp';

export function PhoneSettingsWithOTP() {
  const { userData, setUserData } = useAuth();
  const { t, dir } = useLanguage();
  const { otpMethod } = useOTPMethod();
  
  const [currentStep, setCurrentStep] = useState<PhoneStep>('display');
  const [phone, setPhone] = useState(userData?.phone || '');
  const [originalPhone] = useState(userData?.phone || '');
  const [loading, setLoading] = useState(false);
  
  // OTP states
  const [otpValue, setOtpValue] = useState('');
  const [isOtpLoading, setIsOtpLoading] = useState(false);
  const [isResendLoading, setIsResendLoading] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);
  const [otpExpiry, setOtpExpiry] = useState<Date | null>(null);

  const phoneRegex = /^05\d{8}$/; // 05xxxxxxxx

  // Cooldown timer effect
  useEffect(() => {
    if (resendCooldown > 0) {
      const timer = setTimeout(() => setResendCooldown(prev => prev - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [resendCooldown]);

  // OTP expiry timer effect
  useEffect(() => {
    if (otpExpiry) {
      const timer = setInterval(() => {
        const now = new Date();
        if (now >= otpExpiry) {
          setOtpExpiry(null);
          toast.error(t('otp.errors.otpExpired') || 'Verification code has expired');
          setCurrentStep('edit');
        }
      }, 1000);
      return () => clearInterval(timer);
    }
  }, [otpExpiry, t]);

  const handleStartEdit = () => {
    setCurrentStep('edit');
    setPhone(originalPhone);
  };

  const handleCancelEdit = () => {
    setCurrentStep('display');
    setPhone(originalPhone);
    setOtpValue('');
    setOtpExpiry(null);
    setResendCooldown(0);
  };

  const handleSendOTP = async () => {
    if (!phoneRegex.test(phone)) {
      toast.error(dir === 'rtl' ? 'رقم الجوال يجب أن يكون بصيغة 05XXXXXXXX' : 'Phone must be 05XXXXXXXX');
      return;
    }

    if (phone === originalPhone) {
      toast.info(dir === 'rtl' ? 'لم يتم تغيير رقم الهاتف' : 'Phone number unchanged');
      setCurrentStep('display');
      return;
    }

    try {
      setIsOtpLoading(true);
      const language = dir === 'rtl' ? 'ar' : 'en';
      const result = await sendOTP(phone, language);
      
      if (result.success) {
        setCurrentStep('otp');
        setOtpExpiry(new Date(Date.now() + 5 * 60 * 1000)); // 5 minutes from now
        setResendCooldown(60); // 1 minute cooldown
        toast.success(t('otp.success.otpSent') || (otpMethod === 'sms' ? 'Verification code sent via SMS' : 'Verification code sent to your WhatsApp'));
      } else {
        // Show specific error messages
        let errorMessage = result.error || t('otp.errors.sendFailed') || 'Failed to send verification code';
        
        if (result.error?.includes('already registered')) {
          errorMessage = t('otp.errors.phoneAlreadyRegistered') || errorMessage;
        } else if (result.error?.includes('already reserved')) {
          errorMessage = t('otp.errors.phoneAlreadyReserved') || errorMessage;
        }
        
        toast.error(errorMessage);
      }
    } catch (error) {
      console.error('Error sending OTP:', error);
      toast.error(t('otp.errors.sendFailed') || 'Failed to send verification code');
    } finally {
      setIsOtpLoading(false);
    }
  };

  const handleVerifyOTP = async () => {
    if (otpValue.length !== 4) {
      toast.error(t('otp.errors.otpInvalid') || 'Please enter a valid 4-digit code');
      return;
    }

    try {
      setIsOtpLoading(true);
      const result = await verifyOTP(phone, otpValue);
      
      if (result.success) {
        toast.success(t('otp.success.phoneVerified') || 'Phone number verified successfully');
        await handleFinalSave();
      } else {
        toast.error(result.error || t('otp.errors.otpInvalid') || 'Invalid verification code');
        setOtpValue(''); // Clear invalid OTP
      }
    } catch (error) {
      console.error('Error verifying OTP:', error);
      toast.error(t('otp.errors.otpInvalid') || 'Invalid verification code');
    } finally {
      setIsOtpLoading(false);
    }
  };

  const handleResendOTP = async () => {
    try {
      setIsResendLoading(true);
      const language = dir === 'rtl' ? 'ar' : 'en';
      const result = await resendOTP(phone, language);
      
      if (result.success) {
        setOtpExpiry(new Date(Date.now() + 5 * 60 * 1000)); // 5 minutes from now
        setResendCooldown(60); // 1 minute cooldown
        setOtpValue(''); // Clear current OTP
        toast.success(t('otp.success.otpSent') || (otpMethod === 'sms' ? 'New verification code sent via SMS' : 'New verification code sent to your WhatsApp'));
      } else {
        toast.error(result.error || t('otp.errors.sendFailed') || 'Failed to send verification code');
      }
    } catch (error) {
      console.error('Error resending OTP:', error);
      toast.error(t('otp.errors.sendFailed') || 'Failed to send verification code');
    } finally {
      setIsResendLoading(false);
    }
  };

  const handleFinalSave = async () => {
    if (!userData) return;

    try {
      setLoading(true);
      
      // Use comprehensive phone update function
      const phoneUpdateResult = await updateUserPhoneComprehensive(userData.id, originalPhone, phone);
      
      if (!phoneUpdateResult.success) {
        toast.error(phoneUpdateResult.error || t('settings.phoneUpdateError') || 'Failed to update phone number');
        return;
      }
      
      // Clean up OTP verification record
      await cleanupOTP(phone);
      
      // Update local state
      setUserData({
        ...userData,
        phone
      });
      
      toast.success(t('settings.phoneUpdateSuccess') || 'Phone number updated successfully');
      setCurrentStep('display');
      setOtpValue('');
      setOtpExpiry(null);
      setResendCooldown(0);
    } catch (error) {
      console.error('Error updating phone:', error);
      toast.error(t('settings.phoneUpdateError') || 'Failed to update phone number');
    } finally {
      setLoading(false);
    }
  };

  const handleBackToEdit = () => {
    setCurrentStep('edit');
    setOtpValue('');
    setOtpExpiry(null);
    setResendCooldown(0);
  };

  const renderTimeRemaining = () => {
    if (!otpExpiry) return null;
    
    const now = new Date();
    const remaining = Math.max(0, Math.floor((otpExpiry.getTime() - now.getTime()) / 1000));
    const minutes = Math.floor(remaining / 60);
    const seconds = remaining % 60;
    
    return (
      <p className="text-sm text-muted-foreground text-center">
        {t('otp.info.codeExpiry')?.replace('{{minutes}}', `${minutes}:${seconds.toString().padStart(2, '0')}`) || 
          `Code expires in ${minutes}:${seconds.toString().padStart(2, '0')}`}
      </p>
    );
  };

  const getStepIcon = () => {
    switch (currentStep) {
      case 'display':
        return <Phone className="h-5 w-5" />;
      case 'edit':
        return <Edit className="h-5 w-5" />;
      case 'otp':
        return <MessageSquare className="h-5 w-5" />;
      default:
        return null;
    }
  };

  const getStepTitle = () => {
    switch (currentStep) {
      case 'display':
        return t('settings.phoneNumber') || 'Phone Number';
      case 'edit':
        return t('settings.editPhone') || 'Edit Phone Number';
      case 'otp':
        return t('otp.title') || 'Phone Verification';
      default:
        return '';
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className={`flex items-center gap-2 ${dir === 'rtl' ? 'flex-row-reverse' : ''}`}>
          {getStepIcon()}
          {getStepTitle()}
        </CardTitle>
        {currentStep === 'otp' && (
          <p className="text-sm text-muted-foreground">
            {otpMethod === 'sms'
              ? (t('otp.smsSubtitle') || "We've sent a verification code via SMS")
              : (t('otp.subtitle') || "We've sent a verification code to your WhatsApp")
            }
          </p>
        )}
      </CardHeader>

      <CardContent className="space-y-4">
        {/* Display Mode */}
        {currentStep === 'display' && (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <Label className="text-sm font-medium">
                  {t('settings.currentPhone') || 'Current Phone Number'}
                </Label>
                <p className="text-lg font-mono">{originalPhone || t('settings.noPhone') || 'No phone number'}</p>
              </div>
              <Button onClick={handleStartEdit} variant="outline" size="sm">
                <Edit className={`h-4 w-4 ${dir === 'rtl' ? 'ml-2' : 'mr-2'}`} />
                {t('common.edit') || 'Edit'}
              </Button>
            </div>
          </div>
        )}

        {/* Edit Mode */}
        {currentStep === 'edit' && (
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="phone">
                {t('settings.newPhone') || 'New Phone Number'}
              </Label>
              <Input
                id="phone"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                placeholder="05XXXXXXXX"
                dir="ltr"
                className="text-left"
                inputMode="numeric"
                maxLength={10}
              />
              {phone !== originalPhone && phone && (
                <p className="text-xs text-amber-600">
                  {dir === 'rtl' 
                    ? 'سيتم إرسال رمز تحقق لهذا الرقم' 
                    : 'A verification code will be sent to this number'}
                </p>
              )}
            </div>

            <div className={`flex gap-2 ${dir === 'rtl' ? 'flex-row-reverse' : ''}`}>
              <Button onClick={handleCancelEdit} variant="outline" className="flex-1">
                {t('common.cancel') || 'Cancel'}
              </Button>
              <Button 
                onClick={handleSendOTP}
                disabled={isOtpLoading || !phone}
                className="flex-1"
              >
                {isOtpLoading ? (
                  <>
                    <Loader2 className={`h-4 w-4 animate-spin ${dir === 'rtl' ? 'ml-2' : 'mr-2'}`} />
                    {t('otp.sendOtp') || 'Sending...'}
                  </>
                ) : (
                  phone !== originalPhone && phone 
                    ? (t('otp.sendOtp') || 'Send Verification Code')
                    : (t('common.save') || 'Save')
                )}
              </Button>
            </div>
          </div>
        )}

        {/* OTP Verification Mode */}
        {currentStep === 'otp' && (
          <div className="space-y-4">
            <div className="text-center">
              <p className="text-sm text-muted-foreground mb-4">
                {otpMethod === 'sms'
                  ? (t('otp.info.checkSms') || 'Please check your SMS messages')
                  : (t('otp.info.checkWhatsapp') || 'Please check your WhatsApp messages')
                } ({phone})
              </p>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-2">
                  {t('otp.otpLabel') || 'Verification Code'}
                </label>
                <OTPInput
                  value={otpValue}
                  onChange={setOtpValue}
                  disabled={isOtpLoading}
                  dir={dir}
                />
              </div>

              {renderTimeRemaining()}

              <Button 
                onClick={handleVerifyOTP}
                disabled={isOtpLoading || otpValue.length !== 4}
                className="w-full"
              >
                {isOtpLoading ? (
                  <>
                    <Loader2 className={`h-4 w-4 animate-spin ${dir === 'rtl' ? 'ml-2' : 'mr-2'}`} />
                    {t('otp.verifyOtp') || 'Verifying...'}
                  </>
                ) : (
                  t('otp.verifyOtp') || 'Verify Code'
                )}
              </Button>

              <div className="flex gap-2">
                <Button 
                  type="button"
                  variant="outline"
                  onClick={handleBackToEdit}
                  disabled={isOtpLoading}
                  className="flex-1"
                >
                  <ArrowLeft className={`h-4 w-4 ${dir === 'rtl' ? 'ml-2 rotate-180' : 'mr-2'}`} />
                  {t('common.back') || 'Back'}
                </Button>

                <Button 
                  type="button"
                  variant="outline"
                  onClick={handleResendOTP}
                  disabled={isResendLoading || resendCooldown > 0}
                  className="flex-1"
                >
                  {isResendLoading ? (
                    <Loader2 className={`h-4 w-4 animate-spin ${dir === 'rtl' ? 'ml-2' : 'mr-2'}`} />
                  ) : null}
                  {resendCooldown > 0 
                    ? `${t('otp.resendOtp') || 'Resend'} (${resendCooldown}s)`
                    : t('otp.resendOtp') || 'Resend Code'
                  }
                </Button>
              </div>
            </div>

            {resendCooldown === 0 && (
              <Alert>
                <AlertDescription>
                  {t('otp.info.resendAvailable') || "Didn't receive the code? You can resend after 1 minute"}
                </AlertDescription>
              </Alert>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
